import { fetchInboxSessionFeedback } from './api';
import { canLeavePrivateFeedback, hasListenerFeedback } from './leaveReview';
import { isTalkSessionTransaction } from './sessionPricing';

const txIdOf = tx => {
  const id = tx?.id;
  if (!id) {
    return null;
  }
  return typeof id === 'string' ? id : id.uuid || null;
};

const feedbackRecord = raw => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const thumb = raw.thumb || raw.rating || null;
  if (!thumb && !raw.at && !raw.updatedAt && !raw.note) {
    return null;
  }
  return {
    thumb: thumb || 'up',
    note: typeof raw.note === 'string' ? raw.note : '',
    at: raw.at || raw.updatedAt || null,
    listenerId: raw.listenerId || null,
    talkerId: raw.talkerId || null,
  };
};

/**
 * Completed listener sales that still need Integration metadata attached.
 *
 * @param {Array<Object>} transactions
 * @returns {Array<string>}
 */
export const inboxFeedbackIdsToEnrich = (transactions = []) =>
  (Array.isArray(transactions) ? transactions : [])
    .filter(
      tx =>
        isTalkSessionTransaction(tx) &&
        canLeavePrivateFeedback(tx, 'provider') &&
        !hasListenerFeedback(tx)
    )
    .map(txIdOf)
    .filter(Boolean);

/**
 * Merge `metadata.listenerFeedback` onto inbox/dashboard rows.
 * Marketplace queries often omit Integration metadata, so we fill from
 * `/api/session/inbox-feedback` when a completed sale still looks empty.
 *
 * @param {Array<Object>} transactions
 * @param {Function} [fetchFeedback]
 * @returns {Promise<Array<Object>>}
 */
export const attachInboxListenerFeedback = async (
  transactions,
  fetchFeedback = fetchInboxSessionFeedback
) => {
  const list = Array.isArray(transactions) ? transactions : [];
  const ids = inboxFeedbackIdsToEnrich(list);
  if (ids.length === 0 || typeof fetchFeedback !== 'function') {
    return list;
  }
  try {
    const response = await fetchFeedback(ids);
    const map = response?.data?.feedback || {};
    return list.map(tx => {
      const id = txIdOf(tx);
      const record = feedbackRecord(map[id]);
      if (!id || !record) {
        return tx;
      }
      return {
        ...tx,
        attributes: {
          ...(tx.attributes || {}),
          metadata: {
            ...(tx.attributes?.metadata || {}),
            listenerFeedback: record,
          },
        },
      };
    });
  } catch (e) {
    return list;
  }
};
